/**
 * InstallPromptBanner — bottom sheet that nudges PWA install.
 *
 * Shown after the user has engaged with the app (see useInstallPrompt
 * for visibility logic). On Android/Chrome it shows the OS-native
 * "Install" button wired to the deferred beforeinstallprompt event;
 * on iOS it shows a coaching message about "Add to Home Screen".
 *
 * State source: useInstallPrompt hook.
 * Consumers: App.tsx render (gated on showInstallPrompt + !showWelcome).
 */
import React from "react";
import type { Lang } from "../types/index.ts";
import { t } from "../i18n/index.ts";

export interface InstallPromptBannerProps {
  lang: Lang;
  isIOS: boolean;
  canPromptNatively: boolean;
  onDismiss: () => void;
  onInstall: () => void;
}

export function InstallPromptBanner({ lang, isIOS, canPromptNatively, onDismiss, onInstall }: InstallPromptBannerProps) {
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 45,
      background: "var(--bg-glass)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
      borderTop: "1px solid var(--border-medium)", borderRadius: "20px 20px 0 0",
      padding: "20px 20px calc(16px + env(safe-area-inset-bottom))",
      animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <img src="/icon-192.svg" alt="Fornland" width={32} height={32} style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--border-default)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", letterSpacing: 0.3 }}>{t("install.title", lang)}</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 2 }}>{t("install.description", lang)}</div>
        </div>
      </div>
      {isIOS && (
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 14, padding: "10px 12px", background: "var(--bg-elevated)", borderRadius: "var(--radius-sm)", letterSpacing: 0.2 }}>
          {t("install.iosHint", lang)}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onDismiss} style={{
          flex: 1, padding: "12px 16px", minHeight: 44, border: "1px solid var(--border-medium)", borderRadius: "var(--radius-lg)",
          background: "none", color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.3,
        }}>{t("install.dismiss", lang)}</button>
        {(!isIOS && canPromptNatively) && (
          <button onClick={onInstall} style={{
            flex: 1, padding: "12px 16px", minHeight: 44, border: "none", borderRadius: "var(--radius-lg)",
            background: "var(--btn-primary-bg)", color: "var(--btn-primary-text)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.3,
          }}>{t("install.install", lang)}</button>
        )}
      </div>
    </div>
  );
}
